import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Select,
  InputNumber,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Alert,
  Spin,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { recordWastage, getWastageReasons } from "../../api/wastageApi";
import { getDefaultFlow } from "../../api/productionApi";
import { getAllMachines, getAllJobs } from "../../api/jobApi";

const REASON_CATEGORIES = [
  { value: "machine_error", label: "Machine Error" },
  { value: "operator_error", label: "Operator Error" },
  { value: "material_defect", label: "Material Defect" },
  { value: "setup_waste", label: "Setup Waste" },
  { value: "process_waste", label: "Process Waste" },
  { value: "other", label: "Other" },
];

const WastageEntryForm = ({ onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setInitLoading(true);
    try {
      const [flowData, reasonsData, jobsData, machinesData] =
        await Promise.allSettled([
          getDefaultFlow(),
          getWastageReasons(),
          getAllJobs(),
          getAllMachines(),
        ]);

      if (flowData.status === "fulfilled") {
        const flow = flowData.value;
        setStages(flow.stages || flow.flow?.stages || []);
      }
      if (reasonsData.status === "fulfilled") {
        setReasons(
          Array.isArray(reasonsData.value)
            ? reasonsData.value
            : reasonsData.value.reasons || []
        );
      }
      if (jobsData.status === "fulfilled") {
        setJobs(jobsData.value.jobs || []);
      }
      if (machinesData.status === "fulfilled") {
        const mData = machinesData.value;
        setMachines(
          Array.isArray(mData) ? mData : mData.machines || []
        );
      }
    } catch (error) {
      toast.error("Failed to load form data");
    }
    setInitLoading(false);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        job_id: values.job_id || undefined,
        stage_id: values.stage_id,
        machine_id: values.machine_id || undefined,
        operator_id: values.operator_id || undefined,
        input_quantity: values.input_quantity,
        output_quantity: values.output_quantity,
        reason_category: values.reason_category,
        reason_code: values.reason_code || undefined,
        reason_detail: values.reason_detail || undefined,
        material_cost: values.material_cost || undefined,
        labor_cost: values.labor_cost || undefined,
        shift: values.shift || undefined,
        notes: values.notes || undefined,
      };
      const result = await recordWastage(payload);
      toast.success(
        `Wastage recorded: ${result.wastage_quantity || ""} units (${
          result.wastage_pct?.toFixed(1) || 0
        }%)`
      );
      form.resetFields();
      form.setFieldsValue({ entry_date: dayjs() });
      onSuccess?.();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to record wastage"
      );
    }
    setLoading(false);
  };

  if (initLoading) {
    return (
      <Card title="Record Wastage Entry">
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card title="Record Wastage Entry">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ entry_date: dayjs() }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="stage_id"
              label="Production Stage"
              rules={[{ required: true, message: "Select stage" }]}
            >
              <Select placeholder="Select stage">
                {stages.map((s) => (
                  <Select.Option key={s.id || s.stage_id} value={s.id || s.stage_id}>
                    {s.name || s.stage_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="machine_id" label="Machine">
              <Select placeholder="Select machine" allowClear showSearch optionFilterProp="children">
                {machines.map((m) => (
                  <Select.Option key={m.id} value={m.id}>
                    {m.name} {m.code ? `(${m.code})` : ""}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="job_id" label="Related Job">
              <Select
                placeholder="Select job (optional)"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {jobs.map((j) => (
                  <Select.Option key={j.id} value={j.id}>
                    {j.job_number} - {j.customer?.name || ""}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="input_quantity"
              label="Input Quantity"
              rules={[{ required: true, message: "Enter input qty" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder="Total input"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="output_quantity"
              label="Output Quantity (Good)"
              rules={[{ required: true, message: "Enter output qty" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder="Good output"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.input_quantity !== curr.input_quantity ||
                prev.output_quantity !== curr.output_quantity
              }
            >
              {({ getFieldValue }) => {
                const inp = getFieldValue("input_quantity") || 0;
                const out = getFieldValue("output_quantity") || 0;
                const waste = inp - out;
                const pct = inp > 0 ? ((waste / inp) * 100).toFixed(1) : 0;
                return (
                  <Form.Item label="Wastage (auto)">
                    <InputNumber
                      style={{ width: "100%" }}
                      value={waste > 0 ? waste : 0}
                      disabled
                      addonAfter={`${pct}%`}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="reason_category"
              label="Reason Category"
              rules={[{ required: true, message: "Select category" }]}
            >
              <Select placeholder="Select category">
                {REASON_CATEGORIES.map((r) => (
                  <Select.Option key={r.value} value={r.value}>
                    {r.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="reason_code" label="Reason Code">
              <Select placeholder="Select specific reason" allowClear>
                {reasons.map((r) => (
                  <Select.Option key={r.id || r.code} value={r.code}>
                    {r.name} ({r.category})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="shift" label="Shift">
              <Select placeholder="Select shift" allowClear>
                <Select.Option value="morning">Morning</Select.Option>
                <Select.Option value="afternoon">Afternoon</Select.Option>
                <Select.Option value="night">Night</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="material_cost" label="Material Cost (₹)">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                prefix="₹"
                placeholder="Estimated material cost"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="labor_cost" label="Labor Cost (₹)">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                prefix="₹"
                placeholder="Estimated labor cost"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="reason_detail" label="Reason Detail">
              <Input placeholder="e.g. Roll alignment off during startup" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label="Notes / Description">
          <Input.TextArea rows={2} placeholder="Additional details..." />
        </Form.Item>

        <Alert
          message="Wastage Recording"
          description="Enter the total input and good output quantities — wastage is auto-calculated. Accurate tracking helps identify process improvements."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
          >
            Record Wastage
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default WastageEntryForm;
